import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Routine, RoutineStep } from '@/types'
import { queueSync, getCurrentUserId, generateUUID } from '@/lib/sync'

interface RoutineCompletion {
  routineId: string
  date: string
  completedSteps: string[]
}

interface RoutineState {
  routines: Routine[]
  completions: RoutineCompletion[]

  // Actions
  addRoutine: (routine: Omit<Routine, 'id' | 'userId' | 'createdAt'>) => void
  removeRoutine: (id: string) => void
  updateRoutine: (id: string, updates: Partial<Omit<Routine, 'id' | 'userId' | 'createdAt'>>) => void
  addStep: (routineId: string, step: Omit<RoutineStep, 'id' | 'order'>) => void
  removeStep: (routineId: string, stepId: string) => void
  reorderSteps: (routineId: string, steps: RoutineStep[]) => void
  completeStep: (routineId: string, stepId: string) => void
  uncompleteStep: (routineId: string, stepId: string) => void
  getTodayCompletion: (routineId: string) => RoutineCompletion | undefined
  isRoutineComplete: (routineId: string) => boolean
  getRoutineProgress: (routineId: string) => { completed: number; total: number }
  resetDailyCompletions: () => void
}

const generateId = generateUUID
const getToday = () => new Date().toISOString().split('T')[0]

export const useRoutineStore = create<RoutineState>()(
  persist(
    (set, get) => ({
      routines: [],
      completions: [],

      addRoutine: (routine) => {
        const newRoutine: Routine = {
          id: generateId(),
          userId: getCurrentUserId(),
          name: routine.name,
          type: routine.type,
          steps: routine.steps.map((step, index) => ({
            ...step,
            id: generateId(),
            order: index,
          })),
          createdAt: new Date().toISOString(),
        }

        set((state) => ({
          routines: [...state.routines, newRoutine],
        }))

        // Queue for Supabase sync
        queueSync('routines', 'create', newRoutine.id, {
          user_id: newRoutine.userId,
          name: newRoutine.name,
          type: newRoutine.type,
          steps: newRoutine.steps,
          created_at: newRoutine.createdAt,
        })
      },

      removeRoutine: (id) => {
        set((state) => ({
          routines: state.routines.filter((r) => r.id !== id),
          completions: state.completions.filter((c) => c.routineId !== id),
        }))

        // Queue for Supabase sync
        queueSync('routines', 'delete', id, {})
      },

      updateRoutine: (id, updates) => {
        const routine = get().routines.find((r) => r.id === id)
        if (!routine) return

        const updatedRoutine = { ...routine, ...updates }

        set((state) => ({
          routines: state.routines.map((r) =>
            r.id === id ? updatedRoutine : r
          ),
        }))

        // Queue for Supabase sync
        queueSync('routines', 'update', id, {
          name: updatedRoutine.name,
          type: updatedRoutine.type,
          steps: updatedRoutine.steps,
        })
      },

      addStep: (routineId, step) => {
        set((state) => ({
          routines: state.routines.map((r) => {
            if (r.id !== routineId) return r
            const newStep: RoutineStep = {
              id: generateId(),
              title: step.title,
              order: r.steps.length,
            }
            return { ...r, steps: [...r.steps, newStep] }
          }),
        }))
      },

      removeStep: (routineId, stepId) => {
        set((state) => ({
          routines: state.routines.map((r) => {
            if (r.id !== routineId) return r
            const newSteps = r.steps
              .filter((s) => s.id !== stepId)
              .map((s, index) => ({ ...s, order: index }))
            return { ...r, steps: newSteps }
          }),
        }))
      },

      reorderSteps: (routineId, steps) => {
        set((state) => ({
          routines: state.routines.map((r) =>
            r.id === routineId ? { ...r, steps } : r
          ),
        }))
      },

      completeStep: (routineId, stepId) => {
        const today = getToday()
        set((state) => {
          const existingCompletion = state.completions.find(
            (c) => c.routineId === routineId && c.date === today
          )

          if (existingCompletion) {
            return {
              completions: state.completions.map((c) =>
                c.routineId === routineId && c.date === today
                  ? { ...c, completedSteps: [...c.completedSteps, stepId] }
                  : c
              ),
            }
          } else {
            return {
              completions: [
                ...state.completions,
                { routineId, date: today, completedSteps: [stepId] },
              ],
            }
          }
        })
      },

      uncompleteStep: (routineId, stepId) => {
        const today = getToday()
        set((state) => ({
          completions: state.completions.map((c) =>
            c.routineId === routineId && c.date === today
              ? { ...c, completedSteps: c.completedSteps.filter((id) => id !== stepId) }
              : c
          ),
        }))
      },

      getTodayCompletion: (routineId) => {
        const today = getToday()
        return get().completions.find(
          (c) => c.routineId === routineId && c.date === today
        )
      },

      isRoutineComplete: (routineId) => {
        const state = get()
        const routine = state.routines.find((r) => r.id === routineId)
        if (!routine) return false

        const completion = state.getTodayCompletion(routineId)
        if (!completion) return false

        return completion.completedSteps.length >= routine.steps.length
      },

      getRoutineProgress: (routineId) => {
        const state = get()
        const routine = state.routines.find((r) => r.id === routineId)
        if (!routine) return { completed: 0, total: 0 }

        const completion = state.getTodayCompletion(routineId)
        return {
          completed: completion?.completedSteps.length || 0,
          total: routine.steps.length,
        }
      },

      resetDailyCompletions: () => {
        // Keep only today's completions
        const today = getToday()
        set((state) => ({
          completions: state.completions.filter((c) => c.date === today),
        }))
      },
    }),
    {
      name: 'waifudo-routines',
    }
  )
)
